import React from 'react';
import { Star, User, Calendar } from 'lucide-react';
import { formatDate, formatTimeAgo } from '../../utils/listingUtils';

const ReviewItem = ({ review }) => {
  const {
    buyer_name,
    buyer_profile_picture,
    rating_overall,
    review_text,
    created_at,
    buyer_first_name,
    buyer_last_name,
    conversation_id
  } = review;

  // Get buyer name from available fields
  const displayName = buyer_name || 
    (buyer_first_name && buyer_last_name 
      ? `${buyer_first_name} ${buyer_last_name}`
      : 'Anonymous');

  // Render stars
  const renderStars = (rating) => {
    if (!rating || rating === 0) return null;
    
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={`w-4 h-4 ${
              index < rating
                ? 'fill-current text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Get rating text
  const getRatingText = (rating) => {
    if (!rating || rating === 0) return 'No rating';
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4.0) return 'Very Good';
    if (rating >= 3.0) return 'Good';
    if (rating >= 2.0) return 'Fair';
    return 'Poor';
  };

  return (
    <div className="border-b border-gray-200 py-6 last:border-0">
      <div className="flex items-start justify-between mb-4">
        {/* Reviewer Info */}
        <div className="flex items-start gap-3">
          {buyer_profile_picture ? (
            <img
              src={buyer_profile_picture}
              alt={displayName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
              <User className="w-6 h-6" />
            </div>
          )}
          
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900">{displayName}</h4>
            </div>
            
            <div className="flex items-center gap-4 mt-1">
              {/* Overall Rating */}
              <div className="flex items-center gap-2">
                {renderStars(rating_overall)}
                {rating_overall && (
                  <span className="text-sm font-medium text-gray-900">
                    {rating_overall.toFixed(1)}
                  </span>
                )}
              </div>
              
              {rating_overall && (
                <>
                  <span className="text-sm text-gray-500">{getRatingText(rating_overall)}</span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(created_at)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Time Ago */}
        <span className="text-sm text-gray-500">{formatTimeAgo(created_at)}</span>
      </div>

      {/* Review Text */}
      {review_text ? (
        <p className="text-gray-700 mb-4 whitespace-pre-line">{review_text}</p>
      ) : (
        <p className="text-gray-500 italic mb-4">No written review provided.</p>
      )}
      
      {/* Indicate if this is from chat */}
      {conversation_id && (
        <div className="text-xs text-gray-400 mt-2">
          From chat conversation
        </div>
      )}
    </div>
  );
};

export default ReviewItem;